import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import { PaginatedResponse } from '../../types/api.types';

// AnimalType interface
export interface AnimalType {
  id: number;
  name: string;
}

// Context interface
export interface AnimalTypeContextType {
  animalTypes: AnimalType[];
  isLoading: boolean;
  error: string | null;
  refreshAnimalTypes: () => Promise<void>;
}

// Create the context with a default value
const AnimalTypeContext = createContext<AnimalTypeContextType | undefined>(
  undefined
);

// Create the provider component
interface AnimalTypeProviderProps {
  children: ReactNode;
}

export const AnimalTypeProvider: React.FC<AnimalTypeProviderProps> = ({
  children,
}) => {
  const [animalTypes, setAnimalTypes] = useState<AnimalType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Function to fetch animal types from the API
  const fetchAnimalTypes = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await gekkoApi.get<PaginatedResponse<AnimalType>>(
        '/animal-types',
        {
          queryParams: { page_size: 100 },
        }
      );

      setAnimalTypes(response.items);
    } catch (err) {
      console.error('Error fetching animal types:', err);
      setError('Failed to fetch animal types. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch animal types on mount
  useEffect(() => {
    fetchAnimalTypes();
  }, [fetchAnimalTypes]);

  // Context value
  const value: AnimalTypeContextType = {
    animalTypes,
    isLoading,
    error,
    refreshAnimalTypes: fetchAnimalTypes,
  };

  return (
    <AnimalTypeContext.Provider value={value}>
      {children}
    </AnimalTypeContext.Provider>
  );
};

// Custom hook to use the animal types context
export const useAnimalTypes = () => {
  const context = useContext(AnimalTypeContext);
  if (context === undefined) {
    throw new Error('useAnimalTypes must be used within an AnimalTypeProvider');
  }
  return context;
};
